import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, EyeSlash, PencilSimple } from "@phosphor-icons/react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ClientProfileFields } from "../components/client/ClientProfileFields";
import { RotatingShieldLogo } from "../components/shared/RotatingShieldLogo";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { getClientNavItems } from "../modules/clientNav";

const EMPTY_FORM = {
  first_name: "", last_name: "", email: "", phone: "", mailing_address: "",
  business_name: "", business_address: "",
  payment_bank_name: "", payment_account_holder_name: "", payment_account_number: "", payment_routing_number: "", account_type: "",
  legal_name: "", tax_id: "", tax_id_type: "", tax_mailing_address: "",
};

export default function ClientProfilePage() {
  const { user, updateProfile, changePassword } = useAuth();
  const navItems = getClientNavItems(user?.unlocked_modules, user?.role);
  const isAffiliate = user?.role === "affiliate";

  const [profile, setProfile] = useState(null);
  const [revealed, setRevealed] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [displayName, setDisplayName] = useState(user?.display_name || "");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const loadProfile = () => api.get("/client/profile").then(({ data }) => setProfile(data));

  useEffect(() => {
    loadProfile();
  }, []);

  const toggleReveal = async () => {
    if (revealed) {
      setRevealed(null);
      return;
    }
    try {
      const { data } = await api.get("/client/profile/reveal");
      setRevealed(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const startEdit = () => {
    setForm({
      ...EMPTY_FORM,
      first_name: profile.first_name || "",
      last_name: profile.last_name || "",
      email: profile.email || "",
      phone: profile.phone || "",
      mailing_address: profile.mailing_address || "",
      business_name: profile.business_name || "",
      business_address: profile.business_address || "",
      payment_bank_name: profile.payment_bank_name || "",
      payment_account_holder_name: profile.payment_account_holder_name || "",
      account_type: profile.account_type || "",
      legal_name: profile.legal_name || "",
      tax_id_type: profile.tax_id_type || "",
      tax_mailing_address: profile.tax_mailing_address || "",
    });
    setEditing(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/client/profile", { ...form, tax_id_type: form.tax_id_type || null, account_type: form.account_type || null });
      toast.success("Profile updated");
      await loadProfile();
      setRevealed(null);
      setEditing(false);
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const saveName = async () => {
    const res = await updateProfile(displayName);
    if (res.success) toast.success("Display name updated");
    else toast.error(res.error);
  };

  const savePassword = async () => {
    const res = await changePassword(oldPassword, newPassword);
    if (res.success) {
      toast.success("Password updated successfully");
      setOldPassword("");
      setNewPassword("");
    } else toast.error(res.error);
  };

  return (
    <DashboardLayout navItems={navItems} moduleName="My Profile" moduleBackPath="/client">
      <div data-testid="client-profile-page" className="max-w-4xl">
        <div className="flex items-center gap-4 mb-8">
          <RotatingShieldLogo size={40} />
          <h1 className="font-serif text-3xl text-igc-cream tracking-tight">My Profile</h1>
        </div>

        <div className="bg-igc-surface border border-igc-gold/15 p-6 mb-6" data-testid="account-settings-section">
          <p className="font-serif text-lg text-igc-cream mb-4">Account Settings</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-xs uppercase tracking-widest text-igc-muted mb-2 block">Display Name</label>
              <div className="flex gap-2">
                <Input data-testid="profile-page-display-name-input" value={displayName} onChange={(e) => setDisplayName(e.target.value)} title="The name shown to your team on the portal." className="bg-igc-surfaceLight border-igc-gold/20" />
                <Button data-testid="profile-page-save-name-button" onClick={saveName} title="Saves your new display name." className="bg-igc-gold hover:bg-igc-goldHover text-black">Save</Button>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-igc-muted mb-2 block">Change Password</label>
              <div className="space-y-2">
                <Input data-testid="profile-page-old-password-input" type="password" placeholder="Current password" title="Your current password, to confirm it's really you." value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} className="bg-igc-surfaceLight border-igc-gold/20" />
                <Input data-testid="profile-page-new-password-input" type="password" placeholder="New password" title="The new password you want to use going forward." value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="bg-igc-surfaceLight border-igc-gold/20" />
                <Button data-testid="profile-page-save-password-button" onClick={savePassword} title="Updates your login password." className="w-full bg-igc-gold hover:bg-igc-goldHover text-black">Update Password</Button>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-igc-surface border border-igc-gold/15 p-6" data-testid="profile-details-section">
          {!profile ? (
            <p className="text-igc-muted text-sm">Loading...</p>
          ) : editing ? (
            <form onSubmit={save} className="space-y-6">
              <p className="text-igc-muted text-xs">Leave payment/tax fields blank to keep their current values unchanged.</p>
              <ClientProfileFields form={form} setForm={setForm} isAffiliate={isAffiliate} sensitiveRequired={false} />
              <div className="flex gap-2">
                <Button type="button" data-testid="cancel-edit-profile-button" onClick={() => setEditing(false)} title="Discards your changes without saving." className="flex-1 bg-igc-surfaceLight text-igc-cream hover:bg-igc-surfaceLight/70">Cancel</Button>
                <Button type="submit" data-testid="save-profile-edit-button" disabled={saving} title="Saves your updated profile information." className="flex-1 bg-igc-gold hover:bg-igc-goldHover text-black">{saving ? "Saving..." : "Save Changes"}</Button>
              </div>
            </form>
          ) : profile.exists === false ? (
            <div className="space-y-4" data-testid="no-profile-message">
              <p className="text-igc-muted text-sm">You haven't added your personal, business, and payment details yet.</p>
              <Button data-testid="add-profile-button" onClick={() => { setForm(EMPTY_FORM); setEditing(true); }} title="Opens the form to enter your contact, business, and payment details for the first time." className="w-full bg-igc-gold hover:bg-igc-goldHover text-black">
                Add Profile Information
              </Button>
            </div>
          ) : (
            <div className="space-y-4" data-testid="client-profile-view">
              <p className="text-igc-goldText text-xs uppercase tracking-widest">Personal & Business Information</p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <p><span className="text-igc-muted">Legal First Name:</span> {profile.first_name}</p>
                <p><span className="text-igc-muted">Legal Last Name:</span> {profile.last_name}</p>
                <p><span className="text-igc-muted">Email:</span> {profile.email}</p>
                <p><span className="text-igc-muted">Phone:</span> {profile.phone}</p>
                <p className="col-span-2"><span className="text-igc-muted">Personal Address:</span> {profile.mailing_address}</p>
                <p><span className="text-igc-muted">Business Name:</span> {profile.business_name || "—"}</p>
                <p><span className="text-igc-muted">Business Address:</span> {profile.business_address || "—"}</p>
              </div>
              <div className="border-t border-igc-gold/10 pt-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-igc-goldText text-xs uppercase tracking-widest">Payment & Tax Information (ACH Banking)</p>
                  <button type="button" data-testid="reveal-sensitive-info-button" onClick={toggleReveal} title="Bank account and tax ID numbers are masked by default. Click to temporarily reveal them, click again to hide." className="flex items-center gap-1 text-xs text-igc-muted hover:text-igc-goldText">
                    {revealed ? <EyeSlash size={14} /> : <Eye size={14} />} {revealed ? "Hide" : "Reveal"}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <p><span className="text-igc-muted">Bank:</span> {profile.payment_bank_name || "—"}</p>
                  <p><span className="text-igc-muted">Account Holder:</span> {profile.payment_account_holder_name || "—"}</p>
                  <p data-testid="account-number-display" title="Your bank account number, masked unless revealed above."><span className="text-igc-muted">Account #:</span> {revealed ? revealed.payment_account_number : profile.payment_account_number_masked}</p>
                  <p data-testid="routing-number-display" title="Your bank's routing number, masked unless revealed above."><span className="text-igc-muted">Routing #:</span> {revealed ? revealed.payment_routing_number : profile.payment_routing_number_masked}</p>
                  <p><span className="text-igc-muted">Account Type:</span> {profile.account_type || "—"}</p>
                  {isAffiliate && (
                    <>
                      <p><span className="text-igc-muted">Legal Name:</span> {profile.legal_name || "—"}</p>
                      <p><span className="text-igc-muted">{profile.tax_id_type || "Tax ID"}:</span> <span data-testid="tax-id-display" title="Your SSN or EIN, masked unless revealed above.">{revealed ? revealed.tax_id : profile.tax_id_masked}</span></p>
                      <p className="col-span-2"><span className="text-igc-muted">Tax Mailing Address:</span> {profile.tax_mailing_address || "—"}</p>
                    </>
                  )}
                </div>
                <p className="text-igc-muted text-[11px] mt-3" data-testid="payment-schedule-note" title="When you can expect to actually get paid.">
                  Payment Schedule: Referral and commission payments are processed twice monthly, on the 1st and 15th, directly to your account on file.
                </p>
              </div>
              <Button data-testid="edit-profile-button" onClick={startEdit} title="Opens your profile fields so you can update your contact, business, and payment info." className="w-full bg-igc-gold hover:bg-igc-goldHover text-black flex items-center gap-2 justify-center">
                <PencilSimple size={16} weight="duotone" /> Edit Profile
              </Button>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
